import type { IFrontendServiceContainer } from '@phi/frontend/service-container';

import './TopBar.scss';
import { utility } from '@phi/components';
import { h, defineComponent, PropType } from 'vue';

import AddVertex from './TopBar/AddVertex';
import AddEdge from './TopBar/AddEdge';
import Split from './TopBar/Split';
import Carve from './TopBar/Carve';
import Offset from './TopBar/Offset';

import Dissolve from './TopBar/Dissolve';
import Delete from './TopBar/Delete';
import Fix from './TopBar/Fix';

import Move from './TopBar/Move';
import Scale from './TopBar/Scale';
import Place from './TopBar/Place';

import Popout from './TopBar/Popout';
import Hole from './TopBar/Hole';
import Bridge from './TopBar/Bridge';

import Flatten from './TopBar/Flatten';
import Linear from './TopBar/Linear';
import Circular from './TopBar/Circular';
import Copy from './TopBar/Copy';

import Tangent from './TopBar/Tangent';
import Smooth from './TopBar/Smooth';

import Mirror from './TopBar/Mirror';


import { SettingsButton } from '@features/user-settings';
import Search from './TopBar/Search';
import Info from './TopBar/Info';
import ButtonDropdown, { Toggle } from './ButtonDropdown';
import Popin from './TopBar/Popin';
import PopoutCut from './TopBar/PopoutCut';
import MergeOnEdge from './TopBar/MergeOnEdge';
import ProjectEdges from './TopBar/ProjectEdges';
import SnapToObject from './TopBar/SnapToObject';
import AttachToSTL from './TopBar/AttachToSTL';
import FlipNormal from './TopBar/FlipNormal';
import Invisible from './TopBar/Invisible';
import LinearAsOne from './TopBar/LinearAsOne';
import CircularExact from './TopBar/CircularExact';
import CopyEdge from './TopBar/CopyEdge';
import EditTangentAtVertex from './TopBar/EditTangentAtVertex';
import FixTangentAtVertex from './TopBar/FixTangentAtVertex';
import EdgeToFaceTangency from './TopBar/EdgeToFaceTangency';
import EdgeToPlanePerpendicular from './TopBar/EdgeToPlanePerpendicular';
import EdgeToEdgeParallel from './TopBar/EdgeToEdgeParallel';
import Unsmoothen from './TopBar/Unsmoothen';
import MirrorDeghost from './TopBar/MirrorDeghost';
import SetSymmetry from './TopBar/SetSymmetry';
import Symmetry from './TopBar/Symmetry';
import RemoveSymmetry from './TopBar/RemoveSymmetry';
import { ShowSmoothenOptionsButton } from '@features/smoothness';
import { SubscriptionButton } from '@phi/subscriptions/frontend';
import BooleanOpsUnion from './TopBar/BooleanOpsUnion';
import BooleanOpsIntersection from './TopBar/BooleanOpsIntersection';
import BooleanOpsSubtraction from './TopBar/BooleanOpsSubtraction';
import ShowParasolid from './TopBar/ShowParasolid';
import { vif1 } from '@phi/util/vuetils';

export default defineComponent({
    props: {
        app: { required: true, type: Object as PropType<IFrontendServiceContainer> },
    },

    render() {
        console.log("Subscription status: " + this.app.subscriptions.state.subscription_status.value);
        if (!this.app.settings) return h('div');
        return h('div', {
            class: ['bar', 'top', utility.pointer_yes],
        }, [
            h('div', { class: 'group' }, [
                h(AddVertex, { state: this.app.state, subscriptions: this.app.subscriptions }),
                h(AddEdge, { state: this.app.state, subscriptions: this.app.subscriptions }),
                h(Split, { state: this.app.state, subscriptions: this.app.subscriptions }),
                h(Carve, { state: this.app.state, subscriptions: this.app.subscriptions }),
                h(Offset, { state: this.app.state, subscriptions: this.app.subscriptions }),
            ]),

            h('div', { class: 'group' }, [
                h(ButtonDropdown, {
                    id: 'merge-faces-group',
                    disabled: false,
                    direction: 'under',
                }, {
                    primary: () => h(Dissolve, { state: this.app.state, subscriptions: this.app.subscriptions }),
                    dropdown: (f: Toggle) => h(FlipNormal, {
                        state: this.app.state,
                        'onUpdate:disabled': f,
                        subscriptions: this.app.subscriptions
                    }),
                }),
                h(ButtonDropdown, {
                    id: 'delete-group',
                    disabled: false,
                    direction: 'under',
                }, {
                    primary: () => h(Delete, { state: this.app.state, subscriptions: this.app.subscriptions }),
                    dropdown: (f: Toggle) => h(Invisible, {
                        state: this.app.state,
                        'onUpdate:disabled': f,
                        subscriptions: this.app.subscriptions
                    }),
                }),
                h(Fix, { state: this.app.state, subscriptions: this.app.subscriptions }),
            ]),

            h('div', { class: 'group' }, [
                h(Move, { state: this.app.state, subscriptions: this.app.subscriptions }),
                h(Scale, { state: this.app.state, subscriptions: this.app.subscriptions }),
                h(ButtonDropdown, {
                    id: 'set-vertex-xyz-group',
                    disabled: false,
                    direction: 'under',
                }, {
                    primary: () => h(Place, { state: this.app.state, subscriptions: this.app.subscriptions }),
                    dropdown: (f: Toggle) => [
                        h(ProjectEdges, {
                            state: this.app.state,
                            'onUpdate:disabled': f,
                            subscriptions: this.app.subscriptions
                        }),
                        h(SnapToObject, {
                            state: this.app.state,
                            'onUpdate:disabled': f,
                            subscriptions: this.app.subscriptions
                        }),
                        h(AttachToSTL, {
                            state: this.app.state,
                            'onUpdate:disabled': f,
                            subscriptions: this.app.subscriptions
                        }),
                    ],
                }),
            ]),

            h('div', { class: 'group' }, [
                h(ButtonDropdown, {
                    direction: 'under',
                    id: 'popout-group',
                    disabled: false,
                }, {
                    primary: () => h(Popout, { state: this.app.state, subscriptions: this.app.subscriptions }),
                    dropdown: (f: Toggle) => [
                        h(Popin, {
                            state: this.app.state,
                            'onUpdate:disabled': f,
                            subscriptions: this.app.subscriptions
                        }),
                        h(PopoutCut, {
                            state: this.app.state,
                            'onUpdate:disabled': f,
                            subscriptions: this.app.subscriptions
                        }),
                    ],
                }),
                h(Hole, { state: this.app.state, subscriptions: this.app.subscriptions }),
                h(ButtonDropdown, {
                    direction: 'under',
                    id: 'handle-group',
                    disabled: false,
                }, {
                    primary: () => h(Bridge, { state: this.app.state, subscriptions: this.app.subscriptions }),
                    dropdown: (f: Toggle) => h(MergeOnEdge, {
                        state: this.app.state,
                        'onUpdate:disabled': f,
                        subscriptions: this.app.subscriptions
                    }),
                }),
            ]),

            h('div', { class: 'group' }, [
                h(Flatten, { state: this.app.state, subscriptions: this.app.subscriptions }),
                h(ButtonDropdown, {
                    id: 'make-edge-linear-group',
                    disabled: false,
                    direction: 'under',
                }, {
                    primary: () => h(Linear, { state: this.app.state, subscriptions: this.app.subscriptions }),
                    dropdown: (f: Toggle) => h(LinearAsOne, {
                        state: this.app.state,
                        'onUpdate:disabled': f,
                        subscriptions: this.app.subscriptions
                    }),
                }),
                h(ButtonDropdown, {
                    id: 'circular-group',
                    disabled: false,
                    direction: 'under',
                }, {
                    primary: () => h(Circular, { state: this.app.state, subscriptions: this.app.subscriptions }),
                    dropdown: (f: Toggle) => h(CircularExact, {
                        state: this.app.state,
                        'onUpdate:disabled': f,
                        subscriptions: this.app.subscriptions
                    }),
                }),
                h(ButtonDropdown, {
                    id: 'clone-group',
                    disabled: false,
                    direction: 'under',
                }, {
                    primary: () => h(Copy, { state: this.app.state, subscriptions: this.app.subscriptions }),
                    dropdown: (f: Toggle) => h(CopyEdge, {
                        state: this.app.state,
                        'onUpdate:disabled': f,
                        subscriptions: this.app.subscriptions
                    }),
                }),
            ]),

            h('div', { class: 'group' }, [
                h(ButtonDropdown, {
                    id: 'tangent-constraints-group',
                    disabled: false,
                    direction: 'under',
                }, {
                    primary: () => h(Tangent, { state: this.app.state, subscriptions: this.app.subscriptions}),
                    dropdown: (f: Toggle) => [
                        h(EditTangentAtVertex, {
                            state: this.app.state,
                            'onUpdate:disabled': f,
                            subscriptions: this.app.subscriptions,
                        }),
                        h(FixTangentAtVertex, {
                            state: this.app.state,
                            'onUpdate:disabled': f,
                            subscriptions: this.app.subscriptions,
                        }),
                        h(EdgeToFaceTangency, {
                            state: this.app.state,
                            'onUpdate:disabled': f,
                            subscriptions: this.app.subscriptions,
                        }),
                        h(EdgeToPlanePerpendicular, {
                            state: this.app.state,
                            'onUpdate:disabled': f,
                            subscriptions: this.app.subscriptions,
                        }),
                        h(EdgeToEdgeParallel, {
                            state: this.app.state,
                            'onUpdate:disabled': f,
                            subscriptions: this.app.subscriptions,
                        }),
                    ],
                }),
                h(ButtonDropdown, {
                    id: 'smoothen-group',
                    disabled: false,
                    direction: 'under',
                }, {
                    primary: () => h(Smooth, { state: this.app.state, subscriptions: this.app.subscriptions }),
                    dropdown: (f: Toggle) => [
                        h(Unsmoothen, {
                            state: this.app.state,
                            'onUpdate:disabled': f,
                            subscriptions: this.app.subscriptions
                        }),

                        h(ShowSmoothenOptionsButton),
                    ],
                }),
            ]),

            h('div', { class: 'group' }, [
                h(ButtonDropdown, {
                    id: 'mirror-group',
                    disabled: false,
                    direction: 'under',
                }, {
                    primary: () => h(Mirror, { state: this.app.state, subscriptions: this.app.subscriptions }),
                    dropdown: (f: Toggle) => h(MirrorDeghost, {
                        state: this.app.state,
                        'onUpdate:disabled': f,
                        subscriptions: this.app.subscriptions
                    }),
                }),
                h(ButtonDropdown, {
                    id: 'symmetry-group',
                    disabled: false,
                    direction: 'under',
                }, {
                    primary: () => h(SetSymmetry, { state: this.app.state, subscriptions: this.app.subscriptions }),
                    dropdown: (f: Toggle) => [
                        h(Symmetry, {
                            state: this.app.state,
                            'onUpdate:disabled': f,
                            subscriptions: this.app.subscriptions
                        }),
                        h(RemoveSymmetry, {
                            state: this.app.state,
                            'onUpdate:disabled': f,
                            subscriptions: this.app.subscriptions
                        }),
                    ],
                }),
            ]),

            vif1(this.app.subscriptions.state.subscription_status.value === 'starter' ||
                 this.app.subscriptions.state.subscription_status.value === 'full' ||
                 this.app.subscriptions.state.subscription_status.value === 'trial' )
                (() => h('div', { class: 'group' }, [
                        h(ButtonDropdown, {
                            id: 'boolean-group',
                            disabled: false,
                            direction: 'under',
                        }, {
                            primary: () => h(BooleanOpsUnion, { state: this.app.state, subscriptions: this.app.subscriptions }),
                            dropdown: (f: Toggle) => [
                                h(BooleanOpsIntersection, {
                                    state: this.app.state,
                                    'onUpdate:disabled': f,
                                    subscriptions: this.app.subscriptions
                                }),
                                h(BooleanOpsSubtraction, {
                                    state: this.app.state,
                                    'onUpdate:disabled': f,
                                    subscriptions: this.app.subscriptions
                                }),
                                h(ShowParasolid, {
                                    state: this.app.state,
                                    session_type: this.app.authentication.session_type.value,
                                    'onUpdate:disabled': f,
                                }),

                            ]
                        }),
                ])),

            /*
            h('div', { class: 'group' }, [
                h(BooleanOps, { state: this.app.state, subscriptions: this.app.subscriptions }),
            ]),
            */

            h('div', { class: 'group' }, [
                h(SettingsButton, { loaded: this.app.state.loaded, tut_stage: this.app.state.tut_stage }),
                h(Search, { state: this.app.state }),
            ]),

            h('div', { class: 'group' }, [
                h(SubscriptionButton, { subscriptions: this.app.subscriptions }),

                h(Info, {
                    loaded: this.app.state.loaded,
                    aside: this.app.state.aside,
                    is_demo: this.app.state.is_demo,
                    'onUpdate:aside': x => this.app.state.aside = x,
                }),
            ]),
        ]);
    },
});
